import type {
  FilterConditionPluginSpecification,
} from '@bindoc-projects/filter-condition-domain-lib';
import {
  FilterConditionPluginImpl
} from '@bindoc-projects/filter-condition-domain-lib';

import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';

export const OP_SAAL_CONDITION_ID = 'intuitiveOpSaal';
export const FilterConditionOpSaalPluginType: FilterConditionPluginSpecification<
  Omit<JsonOpSaalPluginDto, 'type'>,
  JsonOpSaalPluginDto,
  OpSaalCondition
> = {
  type: 'intuitiveOpSaal',
  category: 'case',
  displayName: {
    de: 'OP-Saal',
  },
  sortingIndex: 8,
  occurrence: 'single',
  newInstance(): FilterConditionOpSaalPlugin {
    return new FilterConditionOpSaalPlugin([], null);
  },
  fromCondition(
    jsonObj: OpSaalCondition,
  ): FilterConditionPluginImpl<
      Omit<JsonOpSaalPluginDto, 'type'>
    > {
    return new FilterConditionOpSaalPlugin(
        jsonObj.includes ? jsonObj.includes : null,
        jsonObj.excludes ? jsonObj.excludes : null,
      );
  },
  fromObject(
    obj: JsonOpSaalPluginDto,
  ): FilterConditionPluginImpl<
      Omit<JsonOpSaalPluginDto, 'type'>
    > {
    return new FilterConditionOpSaalPlugin(
        obj.includes ? obj.includes : null,
        obj.excludes ? obj.excludes : null,
      );
  },
};

export class FilterConditionOpSaalPlugin extends FilterConditionPluginImpl<
  Omit<JsonOpSaalPluginDto, 'type'>,
  JsonOpSaalPluginDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    Omit<JsonOpSaalPluginDto, 'type'>,
    JsonOpSaalPluginDto,
    OpSaalCondition
  > = FilterConditionOpSaalPluginType;

  constructor(
    protected includes: string[],
    protected excludes: string[],
  ) {
    super(FilterConditionOpSaalPluginType.type);
  }

  override equals(
    other: FilterConditionOpSaalPlugin,
  ): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.includes, other.includes) &&
      BdArrayUtil.equals(this.excludes, other.excludes)
    );
  }

  clone(): FilterConditionOpSaalPlugin {
    return new FilterConditionOpSaalPlugin(
      this.includes ? [...this.includes] : null,
      this.excludes ? [...this.excludes] : null,
    );
  }

  toCondition(): BdCondition | null {
    return new OpSaalCondition(this.includes, this.excludes);
  }

  toJSON(): JsonOpSaalPluginDto | null {
    return {
      type: this.type,
      includes: this.includes ? this.includes : null,
      excludes: this.excludes ? this.excludes : null,
    };
  }

  toValue(): Omit<JsonOpSaalPluginDto, 'type'> {
    return {
      includes: this.includes,
      excludes: this.excludes,
    };
  }

  override valid(): boolean {
    return (this.includes && this.includes.length > 0) || (this.excludes && this.excludes.length > 0);
  }

  validationToText(): string {
    if (!this.includes && !this.excludes) {
      return 'Mindestens einen OP-Saal auswählen';
    }

    if (this.includes && this.includes.length === 0) {
      return 'Mindestens einen OP-Saal auswählen';
    } else if (this.excludes && this.excludes.length === 0) {
      return 'Mindestens einen OP-Saal auswählen';
    } else {
      return '';
    }
  }

  toText(): string | null {
    if (!this.includes && !this.excludes) {
      return '';
    }

    if (this.excludes) {
      return this.excludes.length === 1
        ? 'ein OP-Saal'
        : `${this.excludes.length} OP-Säle`;
    }

    return this.includes.length === 1
      ? 'ein OP-Saal'
      : `${this.includes.length} OP-Säle`;
  }
}

class OpSaalCondition extends BdContentCondition<string> {
  constructor(includes: string[], excludes: string[]) {
    super(OP_SAAL_CONDITION_ID, includes, excludes);
  }
}

interface JsonOpSaalPluginDto {
  type: string;
  includes: string[];
  excludes: string[];
}
